import React, { useEffect, useRef, useState } from "react";
import { Box, ButtonBase, Tooltip, Typography } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import FilterSidebar from "../../Settings/Integrations/FilterSidebar/FilterSidebar";

const scrollTo = (elementId) =>
  document
    .getElementById(elementId)
    ?.scrollIntoView({ behavior: "smooth", block: "start" });

// Distance from the top of the window at which a section counts as reached
// (just under the sticky header).
const REACHED_PX = 120;
// A click holds its highlight until scrolling has been still this long...
const QUIET_MS = 150;
// ...or at most this long.
const MAX_HOLD_MS = 4000;

// Which section is on screen: the last one whose top has scrolled up to
// REACHED_PX (lower down the window near the end of the page, where short
// sections can never reach the top), and at the very end the last one. Worked out from scroll positions (once
// per frame), so it only moves as the page does and never flickers.
// select(id) highlights a section straight away and holds it while the page
// scrolls there, so the highlight does not run through every section on the
// way.
export const useActiveSection = (elementIds) => {
  const [active, setActive] = useState(elementIds[0] || null);
  // After a click the clicked section stays highlighted until the page has
  // stopped scrolling (a long smooth scroll can take well over a second), so
  // the highlight never runs through the sections in between.
  const held = useRef(false);
  const quietTimer = useRef(null);
  const capTimer = useRef(null);
  const release = () => {
    held.current = false;
    clearTimeout(quietTimer.current);
    clearTimeout(capTimer.current);
  };
  // Released once no scroll has happened for QUIET_MS.
  const holdUntilQuiet = () => {
    clearTimeout(quietTimer.current);
    quietTimer.current = setTimeout(release, QUIET_MS);
  };
  const key = elementIds.join("|");

  useEffect(() => {
    setActive((current) =>
      elementIds.includes(current) ? current : elementIds[0] || null
    );
    let frame = null;
    let scroller = null;
    const scrollBox = () =>
      scroller && scroller !== document
        ? scroller
        : document.scrollingElement || document.documentElement;
    const update = () => {
      frame = null;
      if (held.current) return;
      const present = elementIds.filter((id) => document.getElementById(id));
      if (present.length === 0) return;
      const box = scrollBox();
      const left = box.scrollHeight - box.scrollTop - box.clientHeight;
      // Over the last half screen of scrolling the line moves down the
      // window, so short sections near the end are reached one by one too.
      const nearEnd = Math.max(0, 1 - left / (box.clientHeight / 2));
      const line = REACHED_PX + nearEnd * box.clientHeight * 0.6;
      let reached = present[0];
      present.forEach((id) => {
        if (document.getElementById(id).getBoundingClientRect().top <= line)
          reached = id;
      });
      setActive(left <= 2 ? present[present.length - 1] : reached);
    };
    const onScroll = (event) => {
      scroller = event.target;
      // Still moving towards the clicked section: keep holding.
      if (held.current) holdUntilQuiet();
      if (frame === null) frame = window.requestAnimationFrame(update);
    };
    // A clicked section stays selected when its scroll ends; the next scroll
    // by the visitor decides again.
    const onScrollEnd = () => {
      if (held.current) release();
    };
    // Capture: the page may scroll inside a container, not the window.
    document.addEventListener("scroll", onScroll, {
      capture: true,
      passive: true,
    });
    document.addEventListener("scrollend", onScrollEnd, { capture: true });
    return () => {
      release();
      if (frame !== null) window.cancelAnimationFrame(frame);
      document.removeEventListener("scroll", onScroll, { capture: true });
      document.removeEventListener("scrollend", onScrollEnd, { capture: true });
    };
    // `key` stands for the list of ids.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  const select = (id) => {
    held.current = true;
    holdUntilQuiet();
    // Never hold longer than this, whatever happens to the scroll.
    clearTimeout(capTimer.current);
    capTimer.current = setTimeout(release, MAX_HOLD_MS);
    setActive(id);
  };

  return [active, select];
};

// A name on one line, ending in "…" when it does not fit; then the full name
// shows on hover. No tooltip when it fits. (Screen readers get the full name
// anyway: the "…" is only drawn.)
const TruncatedName = ({ name }) => {
  const ref = useRef(null);
  const [cut, setCut] = useState(false);
  const check = () => {
    const el = ref.current;
    setCut(!!el && el.scrollWidth > el.clientWidth + 1);
  };
  return (
    <Tooltip title={cut ? name : ""} placement="right" arrow disableInteractive>
      <Box
        component="span"
        ref={ref}
        onMouseEnter={check}
        sx={{
          minWidth: 0,
          flex: 1,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {name}
      </Box>
    </Tooltip>
  );
};

// Left-hand list of the sections on a tab (resource categories, control
// areas): a quiet list of names with counts, the section on screen
// highlighted in the company's secondary colour; clicking one scrolls to it.
// On phones it is one row of pills that scrolls sideways.
// items: [{ id, name, count }]; `elementId(item)` is the section's DOM id.
const SectionNav = ({ title, label, icon, items, elementId }) => {
  const theme = useTheme();
  const t = theme.trust;
  const [active, setActive] = useActiveSection(items.map(elementId));
  if (items.length === 0) return null;
  // Outside the public page (the admin's Controls tab reuses the controls
  // list) the app's own sidebar, unchanged.
  if (!t)
    return (
      <Box
        sx={{
          width: { xs: "100%", md: 260 },
          flexShrink: 0,
          position: { md: "sticky" },
          top: { md: 96 },
        }}
      >
        <FilterSidebar
          title={title}
          ariaLabel={label}
          icon={
            icon &&
            React.cloneElement(icon, {
              sx: {
                fontSize: 24,
                color: theme.customColors.icons.secondary,
                marginRight: theme.spacing(0.5),
              },
            })
          }
          showAll={false}
          categories={items.map((item) => ({
            key: elementId(item),
            title: item.name,
            count: item.count,
          }))}
          selectedCategory={active}
          onCategorySelect={(key) => {
            setActive(key);
            scrollTo(key);
          }}
        />
      </Box>
    );

  return (
    <Box
      component="nav"
      aria-label={label}
      sx={{
        width: { xs: "100%", md: 248 },
        flexShrink: 0,
        position: { md: "sticky" },
        // Under the nav bar (two rows below lg).
        top: { md: 128, lg: 88 },
      }}
    >
      {/* Wide screens: a small card like the others on the page, one row
          per section; the one on screen on a soft grey with darker text.
          Phones: a row of pills that scrolls sideways. */}
      <Box
        sx={{
          p: { md: 0.75 },
          borderRadius: { md: `${t.radius.container}px` },
          border: { md: `1px solid ${t.line}` },
          backgroundColor: { md: t.surface },
        }}
      >
        <Typography
          sx={{
            display: { xs: "none", md: "block" },
            fontSize: 12.5,
            fontWeight: 500,
            color: t.muted,
            px: 1.25,
            pt: 0.75,
            pb: 0.75,
          }}
        >
          {title}
        </Typography>
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "row", md: "column" },
            gap: { xs: 1, md: 0.25 },
            overflowX: { xs: "auto", md: "visible" },
            pb: { xs: 0.5, md: 0 },
            scrollbarWidth: "none",
            "&::-webkit-scrollbar": { display: "none" },
          }}
        >
          {items.map((item) => {
            const id = elementId(item);
            const selected = active === id;
            return (
              <ButtonBase
                key={id}
                aria-current={selected ? "true" : undefined}
                onClick={() => {
                  setActive(id);
                  scrollTo(id);
                }}
                sx={{
                  flexShrink: 0,
                  justifyContent: { xs: "center", md: "space-between" },
                  alignItems: "center",
                  gap: 1.5,
                  minHeight: { md: 38 },
                  px: { xs: 1.5, md: 1.25 },
                  py: { xs: 0.75, md: 0.5 },
                  fontSize: 14,
                  textAlign: "left",
                  color: selected ? t.ink : t.inkSoft,
                  fontWeight: selected ? 500 : 400,
                  borderRadius: { xs: `${t.radius.pill}px`, md: "8px" },
                  border: {
                    xs: `1px solid ${selected ? t.lineStrong : t.line}`,
                    md: "none",
                  },
                  backgroundColor: selected
                    ? t.paper
                    : { xs: t.surface, md: "transparent" },
                  transition: `background-color 160ms ${t.ease}, color 160ms ${t.ease}`,
                  "&:hover": {
                    backgroundColor: t.paper,
                    color: t.ink,
                  },
                  "&.Mui-focusVisible": {
                    outline: `2px solid ${t.accent}`,
                    outlineOffset: 1,
                  },
                }}
              >
                <TruncatedName name={item.name} />
                <Box
                  component="span"
                  sx={{
                    flexShrink: 0,
                    minWidth: 24,
                    px: 0.75,
                    py: 0.125,
                    borderRadius: `${t.radius.pill}px`,
                    textAlign: "center",
                    fontSize: 12,
                    fontWeight: 500,
                    fontVariantNumeric: "tabular-nums",
                    // The open section's count in the secondary colour.
                    color: selected ? t.secondary : t.muted,
                    backgroundColor: selected
                      ? t.secondarySoft
                      : alpha(t.ink, 0.05),
                  }}
                >
                  {item.count}
                </Box>
              </ButtonBase>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
};

export default SectionNav;
